import type { LivenessChallenge, MovementDirection } from '@kyc/shared';

/**
 * Head-pose signals from 2D face landmarks — §18.
 *
 * We use landmark geometry (not the transformation matrix) so left/right/up/down are
 * defined by pixel positions in the UNMIRRORED frame (§7):
 *   - the subject's LEFT cheek appears on the image's RIGHT side;
 *   - turning left moves the nose toward the image-right face edge.
 *
 * CRITICAL: the "centred" nose position differs per person and per landmark topology,
 * so frontal is NEVER an absolute number. Each capture session calibrates a baseline
 * (median of early samples) and everything is measured as a DELTA from it. This is what
 * makes the straight-hold work on real faces without a tripod.
 */

export interface PoseLandmark {
  x: number;
  y: number;
}

/** MediaPipe FaceLandmarker canonical indices. */
export const LM = {
  noseTip: 1,
  forehead: 10,
  chin: 152,
  subjectRightFaceEdge: 234, // image-left
  subjectLeftFaceEdge: 454, // image-right
  subjectRightEyeOuter: 33,
  subjectLeftEyeOuter: 263,
} as const;

/** Raw pose ratios (no frontal judgement — that needs the session baseline). */
export interface PoseSignals {
  /** 0..1 nose position between image-left and image-right face edges. */
  yawRatio: number;
  /** 0..1 nose position between the eye line and the chin. */
  pitchRatio: number;
}

/** Pose relative to the calibrated session baseline. */
export interface PoseFrame {
  /** + → subject looking to THEIR LEFT (nose toward image right). */
  yawDelta: number;
  /** + → looking DOWN; − → looking UP. */
  pitchDelta: number;
  frontal: boolean;
}

export const POSE_TUNING = {
  /** Frontal bands around the calibrated centre (before widening). */
  yawCenterBand: 0.09,
  pitchCenterBand: 0.09,
  /** Delta from centre that counts as a completed movement. */
  yawMoveThreshold: 0.14,
  pitchMoveThreshold: 0.14,
  /** Consecutive detection samples a movement/centre pose must hold (§18). */
  holdSamples: 2,
  /** Baseline needs this many samples before the gate can open. */
  baselineSamples: 6,
  /**
   * Progressive widening for users whose calibrated centre is slightly off (§25
   * recovery). widenMax is CLAMPED so the widened frontal band can never reach a
   * movement threshold — otherwise a held movement pose would read as "centred" and the
   * return-to-centre step would become vacuous. Invariant (asserted in tests):
   *   yawCenterBand * widenMax  <  yawMoveThreshold
   *   pitchCenterBand * widenMax <  pitchMoveThreshold
   */
  widenStep: 1.12,
  widenMax: 1.25,
} as const;

export function computePoseSignals(landmarks: PoseLandmark[]): PoseSignals | null {
  const nose = landmarks[LM.noseTip];
  const leftEdge = landmarks[LM.subjectRightFaceEdge]; // image-left
  const rightEdge = landmarks[LM.subjectLeftFaceEdge]; // image-right
  const chin = landmarks[LM.chin];
  const eyeR = landmarks[LM.subjectRightEyeOuter];
  const eyeL = landmarks[LM.subjectLeftEyeOuter];
  if (!nose || !leftEdge || !rightEdge || !chin || !eyeR || !eyeL) return null;

  const faceWidth = rightEdge.x - leftEdge.x;
  if (faceWidth <= 1e-6) return null;
  const yawRatio = (nose.x - leftEdge.x) / faceWidth;

  const eyeY = (eyeR.y + eyeL.y) / 2;
  const faceHeight = chin.y - eyeY;
  if (faceHeight <= 1e-6) return null;
  const pitchRatio = (nose.y - eyeY) / faceHeight;

  return { yawRatio, pitchRatio };
}

/**
 * Per-session baseline: median of early "looking at the screen" samples. Median is
 * robust to the odd glance-away during calibration.
 */
export class PoseBaseline {
  private yaws: number[] = [];
  private pitches: number[] = [];

  add(s: PoseSignals): void {
    if (this.yaws.length >= 60) return;
    this.yaws.push(s.yawRatio);
    this.pitches.push(s.pitchRatio);
  }

  get sampleCount(): number {
    return this.yaws.length;
  }

  get ready(): boolean {
    return this.yaws.length >= POSE_TUNING.baselineSamples;
  }

  private static median(values: number[]): number {
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 ? sorted[mid]! : (sorted[mid - 1]! + sorted[mid]!) / 2;
  }

  get yawCenter(): number {
    return this.yaws.length ? PoseBaseline.median(this.yaws) : 0.5;
  }

  get pitchCenter(): number {
    return this.pitches.length ? PoseBaseline.median(this.pitches) : 0.5;
  }

  reset(): void {
    this.yaws = [];
    this.pitches = [];
  }
}

/** Judge a raw sample against the calibrated baseline. `widen` relaxes the frontal band. */
export function poseFrameFrom(s: PoseSignals, baseline: PoseBaseline, widen = 1): PoseFrame {
  const yawDelta = s.yawRatio - baseline.yawCenter;
  const pitchDelta = s.pitchRatio - baseline.pitchCenter;
  return {
    yawDelta,
    pitchDelta,
    frontal:
      Math.abs(yawDelta) <= POSE_TUNING.yawCenterBand * widen &&
      Math.abs(pitchDelta) <= POSE_TUNING.pitchCenterBand * widen,
  };
}

export function movementSatisfied(direction: MovementDirection, p: PoseFrame): boolean {
  switch (direction) {
    case 'left':
      return p.yawDelta >= POSE_TUNING.yawMoveThreshold;
    case 'right':
      return -p.yawDelta >= POSE_TUNING.yawMoveThreshold;
    case 'up':
      return -p.pitchDelta >= POSE_TUNING.pitchMoveThreshold;
    case 'down':
      return p.pitchDelta >= POSE_TUNING.pitchMoveThreshold;
  }
}

// ---------------------------------------------------------------------------
// §18 — challenge tracker: straight-hold on VALID frames only, then the server's
// randomized movement sequence with a return-to-centre between movements.
// ---------------------------------------------------------------------------

export interface LivenessSample {
  timestampMs: number;
  faceCount: number;
  faceInOval: boolean;
  eyesVisible: boolean;
  photoOk: boolean;
  pose: PoseFrame | null;
}

export type ChallengePhase =
  | { kind: 'straight_hold'; validMs: number; requiredMs: number }
  | { kind: 'move'; direction: MovementDirection; index: number }
  | { kind: 'return_center'; nextIndex: number }
  | { kind: 'done' };

export class ChallengeTracker {
  private phase: ChallengePhase;
  private validMs = 0;
  private lastTs: number | null = null;
  private lastValid = false;
  private holdCount = 0;

  constructor(private challenge: LivenessChallenge) {
    this.phase = { kind: 'straight_hold', validMs: 0, requiredMs: challenge.straightHoldMs };
  }

  get current(): ChallengePhase {
    return this.phase;
  }

  /** §18 — a straight-hold frame only counts when every validity rule holds. */
  private frameValid(s: LivenessSample): boolean {
    return s.faceCount === 1 && s.faceInOval && s.eyesVisible && s.photoOk && (s.pose?.frontal ?? false);
  }

  push(s: LivenessSample): ChallengePhase {
    switch (this.phase.kind) {
      case 'straight_hold': {
        const valid = this.frameValid(s);
        if (this.lastTs !== null && valid && this.lastValid) {
          // Cap the credited interval so a detection stall can't fake hold time.
          this.validMs += Math.min(400, s.timestampMs - this.lastTs);
        }
        this.lastTs = s.timestampMs;
        this.lastValid = valid;
        if (this.validMs >= this.challenge.straightHoldMs) {
          this.holdCount = 0;
          this.phase = { kind: 'move', direction: this.challenge.sequence[0]!, index: 0 };
        } else {
          this.phase = { kind: 'straight_hold', validMs: this.validMs, requiredMs: this.challenge.straightHoldMs };
        }
        return this.phase;
      }
      case 'move': {
        // Movements need the face tracked but not "frontal"; photo quality still applies.
        const trackable = s.faceCount === 1 && s.pose !== null;
        if (trackable && movementSatisfied(this.phase.direction, s.pose!)) {
          this.holdCount++;
          if (this.holdCount >= POSE_TUNING.holdSamples) {
            this.holdCount = 0;
            this.phase = { kind: 'return_center', nextIndex: this.phase.index + 1 };
          }
        } else {
          this.holdCount = 0;
        }
        return this.phase;
      }
      case 'return_center': {
        const centered = s.faceCount === 1 && (s.pose?.frontal ?? false);
        if (centered) {
          this.holdCount++;
          if (this.holdCount >= POSE_TUNING.holdSamples) {
            this.holdCount = 0;
            const next = this.challenge.sequence[this.phase.nextIndex];
            this.phase = next
              ? { kind: 'move', direction: next, index: this.phase.nextIndex }
              : { kind: 'done' };
          }
        } else {
          this.holdCount = 0;
        }
        return this.phase;
      }
      case 'done':
        return this.phase;
    }
  }
}
